<template>
    <div class="w-full min-w-0">
        <button
            type="button"
            class="p-dropdown p-component color-swatches__trigger"
            aria-haspopup="dialog"
            :aria-expanded="String(isOpen)"
            @click="toggle"
        >
            <span class="p-dropdown-label p-inputtext flex items-center min-w-0">
                <span class="preset-preview mr-4" :style="swatchStyle(selectedOption, true)"></span>
                <span class="truncate">{{ selectedOption ? optionName(selectedOption) : placeholderText }}</span>
                <span
                    v-if="!selectedOption || selectedOption.custom"
                    class="ml-2 text-sm text-grey-900 opacity-60 uppercase"
                    >{{ hexColor }}</span
                >
            </span>
            <span class="p-dropdown-trigger">
                <span class="p-dropdown-trigger-icon pi pi-chevron-down"></span>
            </span>
        </button>
        <!-- OverlayPanel only positions itself correctly on <body> -->
        <OverlayPanel
            ref="panel"
            append-to="body"
            class="color-swatches__panel"
            :style="{ width: panelWidth }"
            @show="onShow"
            @hide="onHide"
        >
            <div class="color-swatches__grid" role="radiogroup" :aria-label="placeholderText">
                <button
                    v-for="option in options"
                    :key="option.id"
                    type="button"
                    role="radio"
                    class="color-swatches__swatch"
                    :class="{ 'is-active': option.id === selectedId }"
                    :style="swatchStyle(option)"
                    :title="optionName(option)"
                    :aria-label="optionName(option)"
                    :aria-checked="String(option.id === selectedId)"
                    @click="selectOption(option)"
                ></button>
            </div>
            <span class="block text-xs font-Inter-Bold text-grey-900 mt-4 mb-2 pt-3 border-t border-black border-opacity-10">{{ $t('editor.color.custom') }}</span>
            <div
                ref="area"
                class="color-swatches__area"
                :style="{ backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }"
                @pointerdown="startDrag($event, pickArea)"
            >
                <span
                    class="color-swatches__handle"
                    :style="{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: hexColor }"
                ></span>
            </div>
            <div ref="hue" class="color-swatches__hue" @pointerdown="startDrag($event, pickHue)">
                <span
                    class="color-swatches__handle"
                    :style="{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: `hsl(${hsv.h}, 100%, 50%)` }"
                ></span>
            </div>
            <div class="relative mt-3">
                <span class="color-swatches__hex-dot" :style="{ backgroundColor: hexColor }"></span>
                <input
                    :value="hexColor"
                    type="text"
                    maxlength="7"
                    spellcheck="false"
                    class="p-inputtext p-component w-full uppercase pl-10 py-2 text-sm"
                    :aria-label="$t('editor.color.custom_hex')"
                    @change="onHexInput"
                    @keydown.enter="onHexInput"
                />
            </div>
        </OverlayPanel>
    </div>
</template>

<script>
import OverlayPanel from 'primevue/overlaypanel';
import { hexToHsv, hsvToHex, normalizeHex } from '@/components/Editor/Shared/designTokens';

const CUSTOM_NAME = 'Egyedi szín';
const AUTO_SWATCH = 'linear-gradient(135deg, #fff 50%, #001426 50%)';
const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

// Colour field: preset swatches plus a free colour (saturation/hue picker or hex). Emits `select` with an
// option, or { id: null, name, color } for a custom colour.
export default {
    name: 'ColorSwatches',
    components: { OverlayPanel },
    props: {
        options: {
            type: Array,
            required: true,
        },
        selectedId: {
            type: [Number, String],
            default: null,
        },
        color: {
            type: String,
            required: true,
        },
        placeholder: {
            type: String,
            default: null,
        },
    },
    emits: ['select', 'show', 'hide'],
    data() {
        return { isOpen: false, panelWidth: null, hsv: { h: 0, s: 0, v: 0 } };
    },
    computed: {
        selectedOption() {
            return (
                this.options.find((option) => option.id === this.selectedId) || {
                    name: CUSTOM_NAME,
                    labelKey: 'editor.color.custom',
                    custom: true,
                }
            );
        },
        placeholderText() {
            return this.placeholder || this.$t('editor.color.placeholder');
        },
        hexColor() {
            const hex = normalizeHex(this.color);
            return /^[0-9a-f]{6}$/.test(hex) ? `#${hex}` : '#000000';
        },
    },
    watch: {
        // keeps the hue while dragging through grey / black / white, where the colour itself has none
        hexColor: {
            immediate: true,
            handler(hex) {
                if (hsvToHex(this.hsv) !== hex) this.hsv = hexToHsv(hex);
            },
        },
    },
    beforeDestroy() {
        window.removeEventListener('scroll', this.hidePanel);
    },
    methods: {
        // option lists keep `name` (saved with orders) and add `labelKey` for display
        optionName(option) {
            return option.labelKey ? this.$t(option.labelKey) : option.name;
        },
        swatchStyle(option, resolved) {
            if (option.custom || (!option.color && resolved)) return { backgroundColor: this.hexColor };
            return option.color ? { backgroundColor: option.color } : { background: AUTO_SWATCH };
        },
        toggle(event) {
            this.panelWidth = `${event.currentTarget.offsetWidth}px`;
            this.$refs.panel.toggle(event);
        },
        hidePanel() {
            this.$refs.panel.hide();
        },
        selectOption(option) {
            this.$emit('select', { ...option });
            this.hidePanel();
        },
        setHsv(hsv) {
            this.hsv = hsv;
            this.$emit('select', { id: null, name: CUSTOM_NAME, color: hsvToHex(hsv) });
        },
        // pointer capture keeps the drag (and the click ending it) on the picker, so the panel stays open
        startDrag(event, pick) {
            const target = event.currentTarget;
            target.setPointerCapture(event.pointerId);
            pick(event);
            target.onpointermove = pick;
            target.onpointerup = () => {
                target.onpointermove = null;
            };
        },
        pickArea(event) {
            const rect = this.$refs.area.getBoundingClientRect();
            this.setHsv({
                ...this.hsv,
                s: clamp01((event.clientX - rect.left) / rect.width),
                v: 1 - clamp01((event.clientY - rect.top) / rect.height),
            });
        },
        pickHue(event) {
            const rect = this.$refs.hue.getBoundingClientRect();
            this.setHsv({ ...this.hsv, h: clamp01((event.clientX - rect.left) / rect.width) * 359 });
        },
        onHexInput(event) {
            const hex = normalizeHex(event.target.value.trim());
            if (/^[0-9a-f]{6}$/.test(hex)) this.$emit('select', { id: null, name: CUSTOM_NAME, color: `#${hex}` });
            else event.target.value = this.hexColor;
        },
        onShow() {
            this.isOpen = true;
            // OverlayPanel only follows scrolling parents, so a page scroll would leave it floating
            window.addEventListener('scroll', this.hidePanel, { passive: true });
            this.$emit('show', this.$refs.panel);
        },
        onHide() {
            this.isOpen = false;
            window.removeEventListener('scroll', this.hidePanel);
            this.$emit('hide');
        },
    },
};
</script>

<style>
.preset-preview {
    @apply flex items-center justify-center w-8 h-8 rounded border border-black border-opacity-10 text-sm leading-none overflow-hidden box-border;

    min-width: 2rem;
}

.color-swatches__trigger {
    @apply w-full text-left;
}

/* same look as .p-dropdown-panel */
.p-overlaypanel.color-swatches__panel {
    @apply bg-white p-3;

    /* the theme sets `border: 0 none` on .p-overlaypanel */
    border: 1px solid #bdbdbd;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    margin-top: 0.25rem;

    &.p-overlaypanel-flipped {
        margin-top: -0.25rem;
    }

    &::before,
    &::after {
        display: none;
    }

    .p-overlaypanel-content {
        padding: 0;
    }
}

.color-swatches__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, 1.75rem);
    gap: 0.5rem;
}

.color-swatches__swatch {
    @apply w-7 h-7 rounded-full border border-black border-opacity-10 transition;

    &:hover {
        transform: scale(1.1);
    }

    &.is-active {
        box-shadow: 0 0 0 2px #fff, 0 0 0 4px #001426;
    }
}

.color-swatches__area,
.color-swatches__hue {
    @apply relative w-full rounded;

    cursor: crosshair;
    touch-action: none;
}

.color-swatches__area {
    @apply h-28 rounded-md;

    background-image: linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent);
}

.color-swatches__hue {
    @apply h-2.5 mt-3 rounded-full cursor-pointer;

    background: linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00);
}

.color-swatches__handle {
    @apply absolute w-3.5 h-3.5 rounded-full border-2 border-white pointer-events-none;

    top: 50%;
    transform: translate(-50%, -50%);
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.3);
}

.color-swatches__hex-dot {
    @apply absolute w-5 h-5 rounded-full border border-black border-opacity-10 pointer-events-none;

    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
}
</style>
